import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { backupQuery, devicesTableQuery, post } from '../api/protectee'
import { ApiError } from '../api/client'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { deviceColumns } from './columns'
import { ProtecteeDeviceTabs } from './DeviceTabs'

/**
 * Protectee::all_devices - `#allDevices` (masinile cu `Theft > 0`), randul
 * desfasurat cu filele din `getActionTabs`, si widgetul statiilor bifate cu
 * "Backup settings" (`multipleSelectBackupSettings` -> `multipleSelectAssignProfile`).
 */
export const TABLE_ID = 'allDevices'

function SelectedDevicesWidget({ chosen, shown, onHide, onSaved }: { chosen: number[]; shown: boolean; onHide(): void; onSaved(): void }) {
  const open = shown && chosen.length > 0
  return (
    <div className="multiple_check_widget widget" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-module">
        <div className="widget-icon"><i className="fas fa-hdd"></i></div>
        <h2 className="widget-title">Selected Devices Options</h2>
        <div className="widget-action"><button className="btn hide-widget" type="button" onClick={onHide}><i className="fas fa-times"></i></button></div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left"><li data-class-nav="nav-content" className={open ? 'active' : undefined}><a>Backup settings</a></li></ul>
        </div>
        <div className="nav-content" style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}>
          {open ? <AssignMany chosen={chosen} onSaved={onSaved} /> : null}
        </div>
      </div>
    </div>
  )
}

/** devices/multiple_select/backupSettings.php: profilele firmei (citite prin prima masina bifata), atribuite tuturor. Profilul se alege, nu vine ales. */
function AssignMany({ chosen, onSaved }: { chosen: number[]; onSaved(): void }) {
  const q = useQuery(backupQuery(chosen[0]!))
  const [profile, setProfile] = useState('')
  const [busy, setBusy] = useState(false)
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const chosenProfile = profile
  if (q.data.company_profiles.length === 0) {
    return (
      <>
        <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Backup</strong> settings</h4></header>
        <div className="panel-body"><div className="alert alert-danger"><strong>No profiles defined</strong></div></div>
      </>
    )
  }
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Backup</strong> settings</h4></header>
      <div className="panel-body">
        <form className="form-horizontal tk-action-form assignProfile" method="post" action="/protectee/multipleSelectAssignProfile" onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !chosenProfile) return
          setBusy(true)
          try {
            const body = await post(`/protectee/profiles/${chosenProfile}/assign`, { devices: chosen })
            toast(body.message, body.status === 'OK' ? 'Success' : 'Error', body.status === 'OK' ? 'success' : 'error')
            if (body.status === 'OK') onSaved()
          } catch (err) {
            toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
          } finally {
            setBusy(false)
          }
        }}>
          {chosen.map((d) => <input key={d} type="hidden" name="devices[]" value={d} />)}
          <div className="form-group">
            <label className="col-md-3 control-label">Profile</label>
            <div className="col-md-9">
              <select className="form-control" name="profile" value={chosenProfile} onChange={(e) => setProfile(e.target.value)} required>
                <option value="">Select...</option>
                {q.data.company_profiles.map((p) => <option key={p.ID} value={p.ID}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><div className="col-md-9 col-md-offset-3"><button type="submit" className="btn btn-success" disabled={busy || !chosenProfile}>Apply</button></div></div>
        </form>
      </div>
    </>
  )
}

export function ProtecteeDevicesPage() {
  const client = useQueryClient()
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 2, dir: 'asc' }, search: '' })
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [widgetHidden, setWidgetHidden] = useState(false)
  const columns = useMemo(() => deviceColumns(), [])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]!
  const query = useQuery({
    ...devicesTableQuery({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search }),
    placeholderData: keepPreviousData,
  })
  const rows = query.data?.data ?? []

  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setSelected((s) => { const next = new Set(s); if (target.checked) next.add(idNum); else next.delete(idNum); return next })
      setWidgetHidden(false)
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => r.deviceId)) : new Set())
      setWidgetHidden(false)
    }
  }

  return (
    <Content>
      <div id="content" onClick={onTableClick}>
        <DataTable
          id={TABLE_ID}
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(r.deviceId) }))}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <ProtecteeDeviceTabs id={Number(row.deviceId)} />}
        >
          <SelectedDevicesWidget chosen={[...selected]} shown={selected.size > 0 && !widgetHidden} onHide={() => setWidgetHidden(true)} onSaved={() => { setSelected(new Set()); void client.invalidateQueries({ queryKey: ['protectee'] }) }} />
        </DataTable>
      </div>
    </Content>
  )
}
